import { Box, Card, CardContent, Link, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

const cardSx = {
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
  border: '1px solid #EDF2F7',
  borderRadius: '8px',
};

const labelSx = {
  fontSize: 11,
  fontWeight: 600,
  color: '#718096',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  mb: 0.5,
  display: 'block',
};

const valueSx = {
  fontSize: 14,
  fontWeight: 600,
  color: '#2D3748',
};

const linkColor = '#3182CE';
const iconBlue = '#4299E1';

export interface CustomerInformation {
  name: string;
  company: string;
  telephone: string;
  email: string;
  createDate: string;
  address: string;
}

interface CustomerInformationCardProps {
  data?: CustomerInformation;
  /** Optional initials for the header badge (e.g. "B" for Beumer). Omit to hide the badge. */
}

const CustomerInformationCard = ({ data }: CustomerInformationCardProps) => {
  const customerData: CustomerInformation = data || {
    name: 'Arjan Beumer',
    company: 'Mindtime Backup',
    telephone: '090078601',
    email: 'a.beumer@ardila.nl',
    createDate: '2018-06-12',
    address: 'Westervoorde 16, 8121BS Olst, Nederland',
  };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {/* Header: icon + title, and right-aligned magenta badge */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2.5}
          pt={2}
          pb={1.5}
          borderBottom="1px solid #EDF2F7"
          position="relative"
        >
          <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
            <PersonIcon sx={{ fontSize: 18, color: iconBlue }} />
            <Typography variant="h6" style={{ fontWeight: 600, color: '#2D3748' }}>
              Customer Information
            </Typography>
          </Box>
        </Box>

        {/* Content: three-column grid then full-width address */}
        <Box px={2.5} py={2}>
          {/* Row 1: NAME, COMPANY, TELEPHONE */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" sx={labelSx}>
                NAME
              </Typography>
              <Typography variant="body2" sx={valueSx}>
                {customerData.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={labelSx}>
                COMPANY
              </Typography>
              <Typography variant="body2" sx={valueSx}>
                {customerData.company}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={labelSx}>
                TELEPHONE
              </Typography>
              <Typography variant="body2" sx={valueSx}>
                {customerData.telephone}
              </Typography>
            </Box>
          </Box>

          {/* Row 2: EMAIL, CREATE DATE */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" sx={labelSx}>
                EMAIL
              </Typography>
              <Link
                href={`mailto:${customerData.email}`}
                underline="hover"
                sx={{ fontSize: 14, fontWeight: 600, color: linkColor }}
              >
                {customerData.email}
              </Link>
            </Box>
            <Box>
              <Typography variant="caption" sx={labelSx}>
                CREATE DATE
              </Typography>
              <Typography variant="body2" sx={valueSx}>
                {customerData.createDate}
              </Typography>
            </Box>
            <Box />
          </Box>

          {/* Row 3: ADDRESS full width */}
          <Box>
            <Typography variant="caption" sx={labelSx}>
              ADDRESS
            </Typography>
            <Typography variant="body2" sx={valueSx}>
              {customerData.address}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CustomerInformationCard;
